import { IconButton } from '@festina-life/ui/actions'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles, zIndexStyles } from '@festina-life/ui/libs'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import type { ComponentProps, PropsWithChildren } from 'react'

import type { SearchableItemGroup } from './types'
import {
  type CommandPaletteFilters,
  useCommandPaletteLocalStorage,
} from './useCommandPaletteLocalStorage'

const groupToIconName: Record<
  SearchableItemGroup,
  ComponentProps<typeof Icon>['name']
> = {
  command: 'Bolt',
  navigation: 'ArrowRight',
  tenant: 'BuildingOffice2',
  recent: 'Clock',
}

export const Group = ({
  children,
  className = '',
  group,
  groupName,
}: PropsWithChildren & {
  className?: ComponentProps<'div'>['className']
  group: SearchableItemGroup
  groupName: string
}) => {
  const translate = useTranslate()

  const supportsFavourites = group === 'command' || group === 'navigation'

  const [favourites, setFavourites] = useCommandPaletteLocalStorage()

  const handleToggleVisiblity = (type: keyof CommandPaletteFilters) => {
    const copyOfFavourites = structuredClone(favourites)

    copyOfFavourites[group][type] = !copyOfFavourites[group][type]

    setFavourites(copyOfFavourites)
  }

  return (
    <div
      className={cx(
        'flex shrink flex-col overflow-y-auto last:grow',
        paperStyles({ borders: ['left'] }),
        className,
      )}
    >
      <div
        className={cx(
          'sticky top-0 mb-1 flex items-end gap-2 p-2',
          paperStyles({ isFaded: true }),
          zIndexStyles('sticky'),
        )}
      >
        <div className="flex grow items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={groupToIconName[group]} size="md" />
            <h2 className="mb-0 text-base font-bold uppercase tracking-wide">
              {groupName}
            </h2>
          </div>
          {supportsFavourites && (
            <div className="-my-1">
              {/* negative margin to compensate for padding inside IconButton so that the group doesn't grow taller due to IconButton presence */}
              <Tooltip
                tip={
                  favourites[group].shouldHideFavourites
                    ? translate('commandPalette.group.favourites.tooltip.show')
                    : translate('commandPalette.group.favourites.tooltip.hide')
                }
              >
                <IconButton
                  iconName="Star"
                  isSelected={!favourites[group].shouldHideFavourites}
                  onClick={() => handleToggleVisiblity('shouldHideFavourites')}
                />
              </Tooltip>
              <Tooltip
                tip={
                  favourites[group].shouldHideWarnings
                    ? translate('commandPalette.group.warnings.tooltip.show')
                    : translate('commandPalette.group.warnings.tooltip.hide')
                }
              >
                <IconButton
                  iconName="status.warning"
                  isSelected={!favourites[group].shouldHideWarnings}
                  onClick={() => handleToggleVisiblity('shouldHideWarnings')}
                />
              </Tooltip>
              <Tooltip
                tip={
                  favourites[group].shouldHideUnauthorized
                    ? translate(
                        'commandPalette.group.unauthorized.tooltip.show',
                      )
                    : translate(
                        'commandPalette.group.unauthorized.tooltip.hide',
                      )
                }
              >
                <IconButton
                  iconName="NoSymbol"
                  isSelected={!favourites[group].shouldHideUnauthorized}
                  onClick={() =>
                    handleToggleVisiblity('shouldHideUnauthorized')
                  }
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}
